import axios from 'axios';
import { format, UrlObject } from 'url';
import { generateTraceId } from './generateTraceId';
import { addI18nNamespaceToErrorMessage } from './addI18nNamespaceToErrorMessage';
import { AxiosSdkInstance, Token } from '../types';

interface Config {
  url: UrlObject;
  i18nNamespace?: string;
}

export function axiosClientFactory(
  config: Config,
  getTokens?: () => Promise<Token>,
  getTraceId?: () => string,
  removeTokens?: () => void
): AxiosSdkInstance {
  const instance = axios.create({
    baseURL: format(config.url),
    transformResponse: [
      // @ts-ignore
      ...axios.defaults.transformResponse,
      config.i18nNamespace ? addI18nNamespaceToErrorMessage(config.i18nNamespace) : data => data
    ]
  });

  if (getTokens) {
    instance.interceptors.request.use(cfg => {
      return getTokens().then(token => {
        cfg.headers.common.Authorization = `Bearer ${token.accessToken}`;
        return cfg;
      });
    });

    if (removeTokens) {
      instance.interceptors.response.use(
        cfg => cfg,
        error => {
          if (error.response?.status === 401) {
            removeTokens();
          }
          return Promise.reject(error);
        }
      );
    }
  }

  instance.interceptors.request.use(cfg => {
    if (getTraceId != null) {
      cfg.headers.common['X-B3-TraceId'] = getTraceId();
    } else {
      cfg.headers.common['X-B3-TraceId'] = generateTraceId();
    }
    cfg.headers.common['X-B3-SpanId'] = generateTraceId();
    return cfg;
  });

  // @ts-ignore
  instance.send = <T>(url: string, data: any, metadata?: any) => {
    let config: any;

    if (metadata) {
      config = {
        headers: {
          common: {
            'X-UT-GRPC-METADATA': JSON.stringify(metadata)
          }
        }
      };
    }
    return instance.post<T>(url, data, config);
  };

  return instance as AxiosSdkInstance;
}
